<template>
  <div class="myPageTasks">
    <hl-Modal
      v-if="showConfirmModal"
      class="text-center"
      iconClass="budicon-trash text-danger"
      :title="'Er du sikker?'"
    >
      <p class="mb-5">Denne handlingen kan ikke omgjøres.</p>
      <div>
        <button class="btn btn-danger mx-2" @click="doTaskOperation(confirmingEvent)">Bekreft</button>
        <button class="btn btn-outline-secondary mx-2" @click="showConfirmModal = false">Avbryt</button>
      </div>
    </hl-Modal>

    <template v-if="listItems.length > 0">
      <div class="myPageTasks__list" data-cy="mypage_tasks_list">
        <template v-for="item in listItems">
          <MyPageAd
            v-if="item.listType === 'letting' && item.ad.status !== 'stopped'"
            :key="item.id"
            :letting="item"
            @click="
              $router.push({
                name: 'rentalObject_ads',
                params: {
                  landlordId: item.landlord.id,
                  rentalObjectId: item.rentalObject.id,
                },
              })
            "
          />

          <TaskContainer
            v-else-if="item.listType === 'task'"
            :key="item.id"
            :task="item"
            @click="handleTaskButtonClick"
          />
        </template>
      </div>
      <div v-if="list.length > this.maxListLength" class="d-flex justify-content-center mt-5">
        <button class="btn btn-sm btn-outline-secondary" @click="showAll = !showAll">
          {{ showAll ? 'Vis færre' : 'Vis flere' }}
        </button>
      </div>
    </template>
    <p v-else-if="hasAllTasksLoaded">Du har ingen aktiviteter å følge opp</p>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import MyPageAd from '@/components/mypage/MyPageAd';
import MyPageTask from '@/components/mypage/MyPageTask.vue';
import TaskContainer from '@/components/mypage/TaskContainer.vue';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import {
  ProfilesUserMyPageApi,
  ProfilesUserApi,
  ContractSigningApi,
  ContractApi,
  LeaseTerminationApi,
  TakeoverProtocolApi,
  InspectionReportApi,
} from '@modules/husleieapi2';

const contractSigningApi = new ContractSigningApi();
const contractApi = new ContractApi();
const leaseTerminationApi = new LeaseTerminationApi();
const takeoverProtocolApi = new TakeoverProtocolApi();
const profilesUserApi = new ProfilesUserApi();
const inspectionReportApi = new InspectionReportApi();

const actionPriorityOrder = {
  contract_landlord_sign: 60,
  top_sign: 50,
  contract_cancel_signing: 40,
  contract_edit: 30,
  lease_termination_edit: 20,
  deposit_account_termination: 15,
  letting_edit: 10,
};

function getHighestPriorityAction(task) {
  let highestPriorityAction = null;
  let highestPriorityScore = 0;
  task.actions.forEach(action => {
    const score = actionPriorityOrder[action.type] || 0;
    if (!highestPriorityAction || score > highestPriorityScore) {
      highestPriorityAction = action;
      highestPriorityScore = score;
    }
  });
  return highestPriorityAction;
}

function taskCompare(a, b) {
  const aAction = getHighestPriorityAction(a);
  const bAction = getHighestPriorityAction(b);
  const aScore = actionPriorityOrder[aAction ? aAction.type : null];
  const bScore = actionPriorityOrder[bAction ? bAction.type : null];

  // If equal score then newest should go first
  // The oldest records may not be relevant any more and when you just edited a record,
  // you expect it in the top of the list
  if (aScore === bScore) {
    return b.modified.localeCompare(a.modified);
  }

  return bScore - aScore;
}

export default Vue.extend({
  name: 'MyPageList',
  data() {
    return {
      lettings: [],
      tasks: [],
      confirmingEvent: undefined,
      hasAllTasksLoaded: false,
      showConfirmModal: false,
      loadingTasks: {},
      showAll: false,
    };
  },
  mixins: [i18nCommon],
  props: {
    maxListLength: {
      type: Number,
      default: 4,
    },
  },
  components: {
    MyPageAd,
    MyPageTask,
    TaskContainer,
    'hl-Modal': Modal,
  },
  computed: {
    ...mapGetters(['getLandlordId', 'rentalObjects']),

    sortedTasks() {
      return [...this.tasks].sort(taskCompare);
    },
    list() {
      return [...this.lettings, ...this.sortedTasks].filter(item => item);
    },
    listItems() {
      if (this.showAll) {
        return this.list;
      }
      return this.list.slice(0, this.maxListLength);
    },
  },
  methods: {
    ...mapActions(['loadLandlordProfile']),
    async fetchLettings() {
      const api = new ProfilesUserMyPageApi();
      try {
        this.lettings = await api.getProfileUserMyPageLettings();
        this.lettings = this.lettings.map(letting => {
          letting.listType = 'letting';

          return letting;
        });
      } catch (error) {
        this.$toast('Kunne ikke hente annonser', 'error');
      }
    },
    async fetchTasks() {
      try {
        this.tasks = await profilesUserApi.getProfileUserPendingActivities();
        this.tasks = this.tasks.map(task => {
          task.listType = 'task';

          return task;
        });
      } catch (error) {
        this.$toast('Kunne ikke hente aktiviteter.', 'error');
      }

      // add new inspection-report to tasks
      try {
        const inspectionReports = await inspectionReportApi.getInspectionReports();
        for (let i = 0; i < inspectionReports.length; i++) {
          if (inspectionReports[i].status != 'draft') {
            continue;
          }

          const newTask = {
            listType: 'task',
            id: inspectionReports[i].id,
            landlordId: inspectionReports[i].landlordId,
            type: 'ir-' + inspectionReports[i].object.reportType, // 'ir-takeover' || 'ir-moveout'
            status: inspectionReports[i].status,
            modified: inspectionReports[i].modified,
            actions: [
              {
                type: 'ir_edit',
                externalUrl: undefined,
                params: {
                  landlordId: inspectionReports[i].landlordId,
                  tenancyId: inspectionReports[i].tenancyId,
                  rentalObjectId: inspectionReports[i].rentalObjectId,
                  irId: inspectionReports[i].id,
                },
              },
              {
                type: 'ir_delete',
                externalUrl: undefined,
                params: {
                  landlordId: inspectionReports[i].landlordId,
                  tenancyId: inspectionReports[i].tenancyId,
                  rentalObjectId: inspectionReports[i].rentalObjectId,
                  irId: inspectionReports[i].id,
                },
              },
            ],
            rentalObject: {
              name: inspectionReports[i].object.rental_object_street || 'ukjent object',
            },
            tenant: {
              fullName:
                [inspectionReports[i].object?.tenant_given_name, inspectionReports[i].object?.tenant_family_name]
                  .filter(Boolean)
                  .join(' ') || 'ukjent leietaker',
            },
            landlord: {
              id: inspectionReports[i].landlordId,
            },
          };
          this.tasks = [...this.tasks, newTask];
        }
      } catch (error) {
        this.$toast('Kunne ikke hente nye inspection-report aktiviteter.', 'error');
      }
    },
    async doTaskOperation(event) {
      if (event.action === 'contract_edit' && event.externalUrl) {
        return RedirectOperations.goToURL(AppRedirect.CONTRACT_EDIT, { uuid: event.params.taskId }, true);
      }

      if (event.action === 'contract_preview') {
        this.$router.push({
          name: 'contract_preview',
          params: {
            id: event.params.taskId,
          },
        });

        return;
      }

      if (event.externalUrl) {
        return window.open(event.externalUrl, '_blank');
      }

      if (event.action === 'ir_edit') {
        const { landlordId, tenancyId, rentalObjectId, irId } = event.params;
        window.open(`/dashboard/landlord/${landlordId}/tenancy/${tenancyId}/object/${rentalObjectId}/inspection-report/${irId}`, '_blank');
        return;
      }

      let refetchIsRequired = false;

      try {
        this.$set(this.loadingTasks, event.params.taskId, event.action);

        switch (event.action) {
          case 'contract_delete':
            await contractApi.deleteContractSession(event.params.taskId);
            this.tasks = this.tasks.filter(t => t.id !== event.params.taskId);
            this.$toast('Kontrakt slettet.', 'success');
            break;
          case 'contract_cancel_signing':
            await contractSigningApi.cancelContractSigning(event.params.taskId);
            this.tasks = this.tasks.filter(t => t.id !== event.params.taskId);
            // Cancel endpoint does not return the updated data, and will have reset status
            // Frontend will not make any assumptions on the new status -> re-fetch
            refetchIsRequired = true;
            this.$toast('Signering stoppet.', 'success');
            break;
          case 'lease_termination_delete':
            await leaseTerminationApi.deleteLeaseTerminationSession(event.params.taskId);
            this.tasks = this.tasks.filter(t => t.id !== event.params.taskId);
            this.$toast('Oppsigelse kansellert.', 'success');
            break;
          case 'letting_delete':
            await this.$store.dispatch('deleteLetting', {
              lettingId: event.params.taskId,
              landlordId: event.params.landlordId,
            });
            this.tasks = this.tasks.filter(t => t.id !== event.params.taskId);
            this.$toast('Annonse slettet.', 'success');
            break;
          case 'top_delete':
            await takeoverProtocolApi.deleteTOPSession(event.params.taskId);
            this.tasks = this.tasks.filter(t => t.id !== event.params.taskId);
            this.$toast('Overtakelsesprotokoll slettet.', 'success');
            break;
          case 'ir_delete':
            await inspectionReportApi.deleteInspectionReport(event.params.taskId);
            this.tasks = this.tasks.filter(t => t.id !== event.params.taskId);
            this.$toast('Overtakelsesprotokoll slettet.', 'success');
            break;
          case 'ignore_task':
            await profilesUserApi.ignorePendingActivity(event.params.taskId);
            this.tasks = this.tasks.filter(t => t.id !== event.params.taskId);
            this.$toast('Oppgaven er nå skjult', 'success');
            break;
        }
        this.$set(this.loadingTasks, event.params.taskId, '');

        if (refetchIsRequired) {
          this.fetchTasks();
        }
      } catch (error) {
        console.log(error);
        this.$toast('Noe gikk galt.', 'error');
      }
      this.showConfirmModal = false;
    },
    async handleTaskButtonClick(event) {
      if (
        !this.showConfirmModal &&
        [
          'contract_delete',
          'contract_cancel_signing',
          'lease_termination_delete',
          'letting_delete',
          'top_delete',
          'ir_delete',
        ].indexOf(event.action) > -1
      ) {
        this.showConfirmModal = true;
        this.confirmingEvent = event;
      } else {
        this.showConfirmModal = false;
        this.confirmingEvent = event;

        await this.doTaskOperation(event);
      }
    },
  },
  async created() {
    await this.fetchLettings();
    await this.fetchTasks();

    this.hasAllTasksLoaded = true;
  },
});
</script>

<style lang="scss" scoped>
.myPageTasks__list {
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: 1fr;
  gap: 1rem 0rem;
}

@media (min-width: 1000px) {
  .myPageTasks__list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 1fr;
    gap: 1rem;
  }
}
</style>
